import { MessagingConversationsApiService } from '@api_patient/messaging_conversations_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { DateService } from '@application/time/date.service';
import { BookingParticipantRole, BookingParticipantSubjectRole, CaseApiModel, CaseResourceDto } from '@visiba/api-vcs-client-v2';
import { Injectable, Scopes } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { CaseModelShared, Issues } from './case_model_shared';
import type { Participant } from './common';

export interface MessagingData {
	readonly id: string;
	readonly completed: boolean;
	readonly date: VisibaDate;
	readonly participants: Readonly<Participant>[];
	readonly receptionName: string;
	readonly issues: Issues;
	readonly rfv: Nullable<string>;
	readonly representativeNiN: Nullable<string>;
	readonly representativeName: Nullable<string>;
	readonly currentUserSubjectRole: BookingParticipantSubjectRole | null;
	readonly otherRepresentativeNames: string[];
}

export interface MessagingMetaData {
	readonly hasReadUntilTimestamp: VisibaDate | undefined;
	readonly lastMessageTimestamp: VisibaDate | undefined;
	readonly allowClientResponse: boolean;
	readonly allowClientToComplete: boolean;
	readonly completedTimestamp: VisibaDate | undefined;
	readonly receiptUrl: Nullable<string>;
	readonly resourceInfo: Nullable<CaseResourceDto>;
}

interface PresentationInfo {
	readonly data: MessagingData | null;
	readonly metaData: MessagingMetaData | null;
}

@Injectable({
	scope: Scopes.Transient,
})
export class MessagingCaseModel extends CaseModelShared<MessagingData> {
	public static readonly MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX = 'M_';
	public readonly presentation = Presentation.create<PresentationInfo>({
		data: null,
		metaData: null,
	});

	constructor(
		private readonly dateService: DateService,
		private readonly messagingConversationsApiService: MessagingConversationsApiService,
		private readonly analyticService: AnalyticService,
	) {
		super();
	}

	public updateLesserData(caseData: CaseApiModel): void {
		if (caseData.MessageConversationId == null) throw new Error('Invalid messaging');

		const representedPatient = caseData.OtherParticipants.find(
			(participant) => participant.SubjectRole === BookingParticipantSubjectRole.MainSubject,
		);
		const otherRepresentativeNames = caseData.OtherParticipants.filter(
			(participant) => participant.SubjectRole === BookingParticipantSubjectRole.LegalGuardian,
		)
			.map((participant) => participant.Name)
			.filter((name): name is string => name != null);
		const participants: Participant[] = [];
		for (const participant of caseData.OtherParticipants) {
			participants.push({
				role: participant.Role,
				descriptiveTitle: participant.DescriptiveTitle,
				title: participant.Title,
				leftTimestamp: participant.LeftTimestamp,
				// TODO PAT-426: Allow null?
				name: participant.Name ?? '',
				profileImageUrl: participant.ProfileImageUrl,
			});
		}
		if (
			caseData.PreviousOwner != null &&
			!caseData.OtherParticipants.some((participant) => participant.Role === BookingParticipantRole.Host)
		) {
			participants.push({
				role: BookingParticipantRole.Host,
				descriptiveTitle: null,
				title: null,
				leftTimestamp: null,
				name: caseData.PreviousOwner.Name ?? '',
				profileImageUrl: caseData.PreviousOwner.ProfileImageUrl,
			});
		}

		this.presentation.write({
			data: {
				id: caseData.MessageConversationId.toString(),
				completed: caseData.Completed,
				date: this.dateService.newDate(caseData.Time),
				participants: participants,
				receptionName: caseData.ReceptionName ?? '',
				issues: this.parseIssues(caseData),
				rfv: caseData.Condition,
				representativeNiN: caseData.AsAgentFor,
				representativeName: representedPatient?.Name ?? null,
				currentUserSubjectRole: caseData.ActingParticipantSubjectRole,
				otherRepresentativeNames: otherRepresentativeNames,
			},
		});
	}

	public get(): MessagingData {
		const data = this.presentation.read().data;
		if (data == null) throw new Error('Did not expect data to be null');

		return data;
	}

	public updateExtendedData(): void {
		this.messagingConversationsApiService.getMessagingConversation(this.getIdentifier()).then((result) => {
			if (result.err) return;

			const data = result.ok.data;

			const lastMessageTimestamp =
				// TODO PAT-426: Missing type? LastMessageTimestamp
				(data as unknown as { LastMessageTimestamp: string }).LastMessageTimestamp != null
					? // TODO PAT-426: Missing type? LastMessageTimestamp
						this.dateService.newDate((data as unknown as { LastMessageTimestamp: string }).LastMessageTimestamp)
					: undefined;
			const hasReadUntilTimestamp =
				data.HasReadUntilTimestamp != null ? this.dateService.newDate(data.HasReadUntilTimestamp) : undefined;
			const completedTimestamp = data.CompletedTimestamp != null ? this.dateService.newDate(data.CompletedTimestamp) : undefined;

			this.presentation.write({
				metaData: {
					lastMessageTimestamp: lastMessageTimestamp,
					hasReadUntilTimestamp: hasReadUntilTimestamp,
					completedTimestamp: completedTimestamp,
					allowClientResponse: data.AllowClientResponse,
					allowClientToComplete: data.AllowClientToComplete,
					receiptUrl: data.PaymentInfo?.ReceiptUrl ?? null,
					resourceInfo: data.ResourceInfo,
				},
			});
		});
	}

	public getIdentifier(): string {
		const data = this.presentation.read().data;
		if (data?.id == null) throw new Error('data not set');

		return data.id;
	}

	public markAsCompleted(date: VisibaDate): void {
		this.presentation.write((data) => {
			const prev = data.extract();
			if (prev.metaData == null) return prev;

			return {
				metaData: {
					...prev.metaData,
					completedTimestamp: date,
				},
			};
		});
	}

	public markAsRead(): void {
		this.presentation.write((data) => {
			const prev = data.extract();
			if (prev.data == null) return prev;

			const issues = prev.data.issues as Issues;

			return {
				data: {
					...prev.data,
					issues: {
						...issues,
						unreadMessages: 0,
						totalCount: issues.totalCount - issues.unreadMessages,
					} as Issues,
				},
			};
		});
	}

	public analyticQuirk_trackOnMountInVisitsView(): void {
		const data = this.get();

		this.analyticService.trackEvent(AnalyticCategories.Visits, `Opened: Messaging case (${data.completed ? 'completed' : 'ongoing'})`);
	}

	public async closeCase(): Promise<void> {
		const result = await this.messagingConversationsApiService.endMessagingConversation(this.getIdentifier());

		if (result.err) {
			this.analyticService.trackEvent(AnalyticCategories.Visits, 'Failed to close messaging case');
			throw new Error('Failed to close messaging case');
		}

		this.analyticService.trackEvent(AnalyticCategories.Visits, 'Closed messaging case');
	}
}
